import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ArrowLeft, Printer, Car, User, Phone, Wrench, Calendar, Clock, CheckCircle, Circle } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const SERVICE_TYPE_LABELS = {
  'service': 'General Service',
  'rwc': 'RWC (Roadworthy)',
  'tyres': 'Tyres',
  'aircon': 'Aircon',
  'brakes': 'Brakes',
  'suspension': 'Suspension',
  'electrical': 'Electrical',
  'other': 'Other'
};

const CHECKLIST_ITEMS = [
  { key: 'oil_filter', label: 'Oil & Filter' },
  { key: 'air_filter', label: 'Air Filter' },
  { key: 'brakes_checked', label: 'Brakes Checked' },
  { key: 'tyres_checked', label: 'Tyres Checked' },
  { key: 'lights_checked', label: 'Lights Checked' },
  { key: 'wipers_checked', label: 'Wipers Checked' },
  { key: 'fluids_topped', label: 'Fluids Topped Up' },
  { key: 'test_drive', label: 'Test Drive Complete' }
];

export default function JobCardPrint() {
  const { jobCardId } = useParams();
  const navigate = useNavigate();
  const printRef = useRef(null);
  const [jobCard, setJobCard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // Shop branding for the print header — sticks the SM's actual workshop
  // name + ABN on the printed copy instead of the platform name.
  const [shopSettings, setShopSettings] = useState(null);
  // Latest customer doc — pulled as a fallback for fields that the JC
  // snapshot doesn't carry (notably the full structured address).
  const [customerDoc, setCustomerDoc] = useState(null);

  useEffect(() => {
    const fetchJobCard = async () => {
      try {
        // SA site-selector context: this page opens in a NEW TAB, so the
        // selected site must be re-sent as ?site_id= (backend ignores it
        // for non-super-admin roles). Without it, a super admin viewing
        // another workshop hit a 404 → "Failed to load job card".
        let siteQS = '';
        try {
          const sid = localStorage.getItem('asf_selected_site_id');
          siteQS = `?site_id=${encodeURIComponent(sid || 'all_sites')}`;
        } catch (_e) { /* noop */ }
        const jcRes = await fetch(`${BACKEND_URL}/api/job-cards/${jobCardId}${siteQS}`, { credentials: 'include' });
        if (jcRes.ok) {
          const data = await jcRes.json();
          setJobCard(data);
          // Branding + customer fallback — scoped to the JC's OWN site so a
          // super admin printing another workshop's JC gets that shop's header.
          const jcSiteQS = data.site_id ? `?site_id=${encodeURIComponent(data.site_id)}` : siteQS;
          const [shopRes, cRes] = await Promise.all([
            fetch(`${BACKEND_URL}/api/site-settings${jcSiteQS}`, { credentials: 'include' }).catch(() => null),
            data.customer_id
              ? fetch(`${BACKEND_URL}/api/customers/${data.customer_id}${jcSiteQS}`, { credentials: 'include' }).catch(() => null)
              : Promise.resolve(null),
          ]);
          if (cRes && cRes.ok) {
            try { setCustomerDoc(await cRes.json()); } catch (_e) { /* noop */ }
          }
          if (shopRes && shopRes.ok) {
            try { setShopSettings(await shopRes.json()); } catch (_e) { /* noop — header falls back to default */ }
          }
        } else {
          setError('Failed to load job card');
        }
      } catch (err) {
        setError('Failed to connect');
      } finally {
        setLoading(false);
      }
    };

    if (jobCardId) {
      fetchJobCard();
    }
  }, [jobCardId]);

  const handlePrint = () => {
    window.print();
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-AU', {
      weekday: 'short',
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  const formatTime = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleTimeString('en-AU', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-slate-500">Loading job card...</p>
      </div>
    );
  }

  if (error || !jobCard) {
    return (
      <div className="min-h-screen flex items-center justify-center flex-col gap-4">
        <p className="text-red-500">{error || 'Job card not found'}</p>
        <Button variant="outline" onClick={() => navigate(-1)}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Go Back
        </Button>
      </div>
    );
  }

  const checklist = jobCard.quality_checklist || {};

  return (
    <div className="min-h-screen bg-slate-100">
      {/* Header - Hidden during print */}
      <div className="print:hidden bg-white border-b p-4">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <Button variant="ghost" onClick={() => navigate(-1)}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Button>
          <Button onClick={handlePrint} className="bg-blue-600 hover:bg-blue-700">
            <Printer className="h-4 w-4 mr-2" />
            Print Job Card
          </Button>
        </div>
      </div>

      {/* Printable Content */}
      <div className="p-8 print:p-0" ref={printRef}>
        <div className="max-w-[210mm] mx-auto bg-white print:shadow-none shadow-lg">
          {/* A4 Page */}
          <div className="p-8 print:p-6 min-h-[297mm]">
            {/* Header */}
            <div className="flex justify-between items-start border-b-2 border-slate-800 pb-4 mb-6">
              <div>
                <h1 className="text-2xl font-bold text-slate-900">SERVICE JOB CARD</h1>
                <p className="text-slate-700 font-semibold mt-1" data-testid="jcp-shop-name">
                  {shopSettings?.business_name || shopSettings?.shop_name || 'Workshop'}
                </p>
                {shopSettings?.abn && (
                  <p className="text-xs text-slate-500">ABN {shopSettings.abn}</p>
                )}
                {(shopSettings?.address || shopSettings?.address_line1) && (
                  <p className="text-xs text-slate-500">
                    {shopSettings.address_line1 || shopSettings.address}
                    {shopSettings.suburb ? `, ${shopSettings.suburb}` : ''}
                    {shopSettings.postcode ? ` ${shopSettings.postcode}` : ''}
                  </p>
                )}
                {shopSettings?.phone && (
                  <p className="text-xs text-slate-500">Tel: {shopSettings.phone}</p>
                )}
              </div>
              <div className="text-right">
                <p className="text-2xl font-mono font-bold text-blue-600">{jobCard.job_card_number}</p>
                <p className="text-sm text-slate-500 mt-1">{formatDate(jobCard.created_at)}</p>
              </div>
            </div>

            {/* Customer & Vehicle Info */}
            <div className="grid grid-cols-2 gap-6 mb-6">
              {/* Customer Info */}
              <div className="border rounded-lg p-4">
                <h2 className="font-bold text-slate-800 mb-3 flex items-center gap-2">
                  <User className="h-4 w-4" />
                  Customer Details
                </h2>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-slate-500">Name:</span>
                    <span className="font-medium" data-testid="jcp-customer-name">{jobCard.customer_name || 'N/A'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-500">Phone:</span>
                    <span className="font-medium" data-testid="jcp-customer-phone">{jobCard.customer_phone || 'N/A'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-500">Email:</span>
                    <span className="font-medium text-xs" data-testid="jcp-customer-email">{jobCard.customer_email || customerDoc?.email || 'N/A'}</span>
                  </div>
                  {(() => {
                    // Compose a multi-line address from the structured
                    // snapshot fields (kiosk-captured). Falls back to the
                    // latest customer doc for direct-creation JCs where the
                    // snapshot fields were never populated, then to the
                    // legacy single-line `customer_address`.
                    const fallback = customerDoc || {};
                    const line1 = (jobCard.customer_address_line1 || fallback.address_line1 || '').trim();
                    const line2 = (jobCard.customer_address_line2 || fallback.address_line2 || '').trim();
                    const suburb = (jobCard.customer_suburb || fallback.suburb || '').trim();
                    const state = (jobCard.customer_state || fallback.state || '').trim();
                    const postcode = (jobCard.customer_postcode || fallback.postcode || '').trim();
                    const locality = [state, postcode].filter(Boolean).join(' ').trim();
                    const localityLine = suburb && locality ? `${suburb}, ${locality}` : (suburb || locality);
                    const lines = [line1, line2, localityLine].filter(Boolean);
                    if (lines.length === 0) {
                      const legacy = (jobCard.customer_address || fallback.address || '').trim();
                      if (legacy) lines.push(legacy);
                    }
                    if (lines.length === 0) {
                      return (
                        <div className="flex justify-between">
                          <span className="text-slate-500">Address:</span>
                          <span className="font-medium text-xs text-slate-400 italic">Not provided</span>
                        </div>
                      );
                    }
                    return (
                      <div className="flex justify-between gap-3" data-testid="jcp-customer-address">
                        <span className="text-slate-500">Address:</span>
                        <span className="font-medium text-xs text-right whitespace-pre-line">
                          {lines.join('\n')}
                        </span>
                      </div>
                    );
                  })()}
                </div>
              </div>

              {/* Vehicle Info */}
              <div className="border rounded-lg p-4">
                <h2 className="font-bold text-slate-800 mb-3 flex items-center gap-2">
                  <Car className="h-4 w-4" />
                  Vehicle Details
                </h2>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-slate-500">Rego:</span>
                    <span className="font-bold font-mono text-lg">{jobCard.vehicle_rego || 'N/A'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-500">Make:</span>
                    <span className="font-medium">{jobCard.vehicle_make || 'N/A'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-500">Model:</span>
                    <span className="font-medium">{jobCard.vehicle_model || 'N/A'}</span>
                  </div>
                  {jobCard.vehicle_year && (
                    <div className="flex justify-between">
                      <span className="text-slate-500">Year:</span>
                      <span className="font-medium">{jobCard.vehicle_year}</span>
                    </div>
                  )}
                  <div className="flex justify-between">
                    <span className="text-slate-500">Odometer (in):</span>
                    <span className="font-medium">{jobCard.odometer_km ? `${jobCard.odometer_km.toLocaleString('en-AU')} km` : '___ km'}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Service Details */}
            <div className="border rounded-lg p-4 mb-6">
              <h2 className="font-bold text-slate-800 mb-3 flex items-center gap-2">
                <Wrench className="h-4 w-4" />
                Service Details
              </h2>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-slate-500 mb-1">Service Types:</p>
                  <div className="flex flex-wrap gap-1">
                    {(jobCard.service_types || [jobCard.service_type]).map((type, i) => (
                      <span key={i} className="bg-blue-100 text-blue-800 px-2 py-0.5 rounded text-xs font-medium">
                        {SERVICE_TYPE_LABELS[type] || type}
                      </span>
                    ))}
                  </div>
                </div>
                <div>
                  <p className="text-slate-500 mb-1">Status:</p>
                  <span className="bg-slate-100 text-slate-800 px-2 py-0.5 rounded text-xs font-medium uppercase">
                    {jobCard.status?.replace(/_/g, ' ')}
                  </span>
                </div>
                <div>
                  <p className="text-slate-500 mb-1">Assigned To:</p>
                  <p className="font-medium">{jobCard.assigned_to_name || 'Unassigned'}</p>
                </div>
                <div>
                  <p className="text-slate-500 mb-1">Booked:</p>
                  <p className="font-medium">
                    {formatDate(jobCard.booked_datetime)}
                    {jobCard.booked_datetime ? (
                      <span className="text-slate-500"> · {formatTime(jobCard.booked_datetime)}</span>
                    ) : null}
                  </p>
                </div>
              </div>
              
              {jobCard.comments && (
                <div className="mt-4 pt-4 border-t">
                  <p className="text-slate-500 mb-1 text-sm">Customer Notes:</p>
                  <p className="text-sm whitespace-pre-wrap">{jobCard.comments}</p>
                </div>
              )}
              {/* iter138.9v — SM / FM working notes. Mechanics often need
                  these on the printed sheet to know any owner-handover
                  conditions, special handling, parts-on-order context.
                  Renders only when notes exist so blank JCs stay clean. */}
              {jobCard.sm_notes && (
                <div className="mt-4 pt-4 border-t">
                  <p className="text-slate-500 mb-1 text-sm">SM / FM Notes:</p>
                  <p className="text-sm whitespace-pre-wrap" data-testid="jcp-sm-notes">{jobCard.sm_notes}</p>
                </div>
              )}
            </div>

            {/* Tasks / Line Items — approved by SM from Quote Builder.
                CRITICAL: this is the list of jobs the mechanic actually
                needs to perform — without it the printout is useless.
                Renders one row per line with a tick-box for the mechanic
                to mark off on paper, the description, qty + quoted hours. */}
            {(jobCard.line_items || []).length > 0 && (
              <div className="border rounded-lg p-4 mb-6" data-testid="jcp-tasks-section">
                <h2 className="font-bold text-slate-800 mb-3 flex items-center gap-2">
                  <Wrench className="h-4 w-4" />
                  Tasks ({jobCard.line_items.length})
                </h2>
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-slate-300 text-slate-500 text-xs uppercase">
                      <th className="text-left pb-2 w-8">Done</th>
                      <th className="text-left pb-2 w-8">#</th>
                      <th className="text-left pb-2">Description</th>
                      <th className="text-left pb-2 w-28">Supplier</th>
                      <th className="text-center pb-2 w-14">OP/PO</th>
                      <th className="text-right pb-2 w-12">Qty</th>
                      <th className="text-right pb-2 w-20">Quoted hrs</th>
                    </tr>
                  </thead>
                  <tbody>
                    {jobCard.line_items.map((item, i) => (
                      <tr key={item.line_id || i} className="border-b border-slate-100 align-top">
                        <td className="py-2">
                          {item.completed ? (
                            <CheckCircle className="h-4 w-4 text-green-600" />
                          ) : (
                            // Empty square so mechanic can tick by hand
                            <span className="inline-block w-4 h-4 border-2 border-slate-400 rounded-sm" />
                          )}
                        </td>
                        <td className="py-2 text-slate-500">{i + 1}</td>
                        <td className="py-2">
                          <div className="font-medium text-slate-800">{item.description || '—'}</div>
                          {item.notes && (
                            <div className="text-xs text-slate-500 mt-0.5 whitespace-pre-wrap">{item.notes}</div>
                          )}
                          {item.completed && item.completed_at && (
                            <div className="text-[10px] text-green-700 mt-0.5">
                              ✓ Completed {formatDate(item.completed_at)}
                            </div>
                          )}
                        </td>
                        {/* iter138 — Supplier column. SM enters this on the
                            quote/JC line so the mechanic knows where the
                            part came from when they collect it. Cost price
                            (ORP) stays SM-only and is intentionally NOT
                            printed. */}
                        <td className="py-2 text-xs text-slate-700 whitespace-nowrap"
                            data-testid={`jcp-supplier-${item.line_id || i}`}>
                          {item.parts_supplier ? (
                            <span className="inline-block px-1.5 py-0.5 rounded bg-emerald-50 border border-emerald-200 text-emerald-800 font-medium">
                              {item.parts_supplier}
                            </span>
                          ) : (
                            <span className="text-slate-300">—</span>
                          )}
                        </td>
                        {/* OP/PO — parts-order status pill, same semantics as
                            the kanban strip: PO (green) = parts ordered,
                            OP (amber) = still to order. Only rendered when a
                            supplier/part is involved, else a dash. */}
                        <td className="py-2 text-center" data-testid={`jcp-oppo-${item.line_id || i}`}>
                          {item.parts_ordered ? (
                            <span
                              className="inline-block px-1.5 py-0.5 rounded bg-green-100 border border-green-300 text-green-800 text-[10px] font-bold"
                              title={`Parts ordered ${item.parts_ordered_at ? formatDate(item.parts_ordered_at) : ''}`}
                            >
                              PO
                            </span>
                          ) : (
                            <span className="inline-block px-1.5 py-0.5 rounded bg-amber-50 border border-amber-300 text-amber-800 text-[10px] font-bold">
                              OP
                            </span>
                          )}
                        </td>
                        {/* POQ removed (iter246) — Qty column is the quantity source. */}
                        <td className="py-2 text-right tabular-nums">{item.quantity ?? 1}</td>
                        <td className="py-2 text-right tabular-nums">
                          {item.quoted_labour_hours
                            ? `${parseFloat(item.quoted_labour_hours).toFixed(2)} h`
                            : '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="font-semibold text-slate-800">
                      <td colSpan={6} className="pt-3 text-right">Total quoted hours:</td>
                      <td className="pt-3 text-right tabular-nums">
                        {jobCard.line_items
                          .reduce((sum, it) => sum + (parseFloat(it.quoted_labour_hours) || 0), 0)
                          .toFixed(2)} h
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            )}

            {/* Quality Checklist */}
            <div className="border rounded-lg p-4 mb-6">
              <h2 className="font-bold text-slate-800 mb-3 flex items-center gap-2">
                <CheckCircle className="h-4 w-4" />
                Quality Checklist
              </h2>
              <div className="grid grid-cols-4 gap-2">
                {CHECKLIST_ITEMS.map((item) => (
                  <div key={item.key} className="flex items-center gap-2 text-sm">
                    {checklist[item.key] ? (
                      <CheckCircle className="h-4 w-4 text-green-600" />
                    ) : (
                      <Circle className="h-4 w-4 text-slate-300" />
                    )}
                    <span className={checklist[item.key] ? 'text-slate-800' : 'text-slate-400'}>
                      {item.label}
                    </span>
                  </div>
                ))}
              </div>
              {checklist.next_service_date && (
                <div className="mt-3 pt-3 border-t">
                  <p className="text-sm">
                    <span className="text-slate-500">Next Service Due:</span>{' '}
                    <span className="font-medium">{formatDate(checklist.next_service_date)}</span>
                  </p>
                </div>
              )}
            </div>

            {/* Work Performed Section - Blank for filling in */}
            <div className="border rounded-lg p-4 mb-6">
              <h2 className="font-bold text-slate-800 mb-3">Work Performed</h2>
              {jobCard.work_performed ? (
                <p className="text-sm whitespace-pre-wrap">{jobCard.work_performed}</p>
              ) : (
                <div className="min-h-[80px] border-b border-dashed border-slate-300" />
              )}
            </div>

            {/* Findings Section */}
            <div className="border rounded-lg p-4 mb-6">
              <h2 className="font-bold text-slate-800 mb-3">Findings / Recommendations</h2>
              {jobCard.findings ? (
                <p className="text-sm whitespace-pre-wrap">{jobCard.findings}</p>
              ) : (
                <div className="min-h-[80px] border-b border-dashed border-slate-300" />
              )}
            </div>

            {/* Hours & Sign Off */}
            <div className="grid grid-cols-3 gap-4 mt-6">
              <div className="border rounded-lg p-4">
                <p className="text-slate-500 text-sm mb-1">Hours Worked:</p>
                <p className="text-2xl font-bold">
                  {jobCard.actual_hours_worked
                    ? `${parseFloat(jobCard.actual_hours_worked).toFixed(2)}`
                    : (jobCard.hours_worked || '___')} hrs
                </p>
              </div>
              <div className="border rounded-lg p-4">
                <p className="text-slate-500 text-sm mb-1">Mechanic Signature:</p>
                <div className="h-12 border-b border-slate-300 mt-2" />
              </div>
              <div className="border rounded-lg p-4">
                <p className="text-slate-500 text-sm mb-1">Customer Signature:</p>
                <div className="h-12 border-b border-slate-300 mt-2" />
              </div>
            </div>

            {/* Footer */}
            <div className="mt-8 pt-4 border-t text-center text-xs text-slate-400">
              <p>Job Card #{jobCard.job_card_number} • Printed {new Date().toLocaleDateString('en-AU')}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Print Styles */}
      <style>{`
        @media print {
          @page {
            size: A4;
            margin: 10mm;
          }
          body {
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }
          .print\\:hidden {
            display: none !important;
          }
        }
      `}</style>
    </div>
  );
}
